import React, { useRef, useState, useEffect } from "react";
import { quotes as quotesApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2 } from "lucide-react";
import { IDS } from "@/constants/testIds";
import PhotoTile from "./PhotoTile";

/**
 * Photo attachments per quote.
 * Requires a persisted quoteId (uploads go straight to storage).
 */
export default function PhotoUploader({ quoteId, initialPhotos = [], disabled = false, onChange }) {
  const [photos, setPhotos] = useState(initialPhotos || []);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setPhotos(initialPhotos || []); }, [initialPhotos]);

  const notify = (list) => { onChange?.(list); };

  const handleFiles = async (files) => {
    if (!quoteId) {
      toast.error("Save the quote first, then add photos");
      return;
    }
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setBusy(true);
    let next = [...photos];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: not an image`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name}: too large (max 8 MB)`);
        continue;
      }
      try {
        const { data } = await quotesApi.uploadPhoto(quoteId, f);
        next = [...next, data];
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setPhotos(next);
    notify(next);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (photoId) => {
    if (!quoteId) return;
    try {
      await quotesApi.deletePhoto(quoteId, photoId);
      const next = photos.filter((p) => p.id !== photoId);
      setPhotos(next);
      notify(next);
    } catch (e) {
      toast.error("Could not remove photo");
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
          Photos <span className="normal-case tracking-normal text-[#94A3B8]">({photos.length})</span>
        </h2>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            data-testid={IDS.editor.photoInput}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={disabled || busy}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || busy || !quoteId}
            className="rounded-full border-slate-300 h-9 text-[#001F3F]"
            title={!quoteId ? "Save the quote first" : ""}
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <ImagePlus size={14} className="mr-2" />}
            Add photos
          </Button>
        </div>
      </div>

      {!quoteId && (
        <div className="text-xs text-[#64748B] bg-slate-50 border border-slate-200 rounded-md p-3">
          Save the quote first, then you can attach photos.
        </div>
      )}

      {quoteId && photos.length === 0 && (
        <div className="text-xs text-[#64748B] bg-slate-50 border border-dashed border-slate-300 rounded-md p-6 text-center">
          No photos yet. Add before/after shots, materials, or site pics — customers love context.
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <PhotoTile
              key={p.id}
              quoteId={quoteId}
              photo={p}
              disabled={disabled}
              onRemove={remove}
              onCaptionChange={(pid, caption) => {
                const next = photos.map((x) => (x.id === pid ? { ...x, caption } : x));
                setPhotos(next);
                notify(next);
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
